import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getStudent, updateStudent, uploadStudentProfileImage } from '../../services/studentService';
import { getSchools } from '../../services/schoolService';
import { getUsers } from '../../services/userService';
import Loader from '../common/Loader';
import ProfileImageUpload from '../common/ProfileImageUpload';
import DatePicker from '../common/DatePicker';
import { SchoolHint, TeacherHint } from './SchoolTeacherHints';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const TEACHER_ROLE_ID = 2;

const inputCls =
  'w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/60 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors text-sm';

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-xl p-4 sm:p-5 space-y-4">
      <h3 className="text-gray-900 dark:text-white font-semibold text-sm">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function EditStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasProfileImage, setHasProfileImage] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [form, setForm] = useState({
    student_id: '',
    student_name: '',
    grade: '',
    school_id: '',
    sped_teacher_id: '',
    date_of_birth: '',
    iep_due_date: '',
    reevaluation_due_date: '',
  });

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'school_id') next.sped_teacher_id = '';
      return next;
    });
  };

  useEffect(() => {
    getSchools({ page: 1, limit: 100, status: 'active' })
      .then(({ data }) => setSchools(data.data?.schools || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.school_id) {
      setTeachers([]);
      return;
    }
    getUsers({
      role: TEACHER_ROLE_ID,
      school_id: parseInt(form.school_id, 10),
      status: 'active',
      page: 1,
      limit: 100,
    })
      .then(({ data }) => setTeachers(data.data?.users || []))
      .catch(() => setTeachers([]));
  }, [form.school_id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getStudent(id);
        const s = data.data;
        setForm({
          student_id: s.student_id || '',
          student_name: s.student_name || '',
          grade: s.grade || '',
          school_id: s.school_id ? String(s.school_id) : '',
          sped_teacher_id: s.sped_teacher_id ? String(s.sped_teacher_id) : '',
          date_of_birth: s.date_of_birth || '',
          iep_due_date: s.iep_due_date || '',
          reevaluation_due_date: s.reevaluation_due_date || '',
        });
        setHasProfileImage(!!s.has_profile_image);
      } catch {
        setError('Failed to load student data.');
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [id]);

  const handleImageSelect = async (file) => {
    setProfileImage(file);
    setImageUploading(true);
    try {
      await uploadStudentProfileImage(id, file);
      setHasProfileImage(true);
      setImageRefreshKey((k) => k + 1);
      setProfileImage(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload profile image.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setHasProfileImage(false);
    setProfileImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.school_id || !form.sped_teacher_id) {
      setError('School and SPED teacher are required.');
      return;
    }
    if (!form.date_of_birth?.trim()) {
      setError('Date of Birth is required.');
      return;
    }
    setLoading(true);

    const payload = {
      ...form,
      school_id: parseInt(form.school_id, 10),
      sped_teacher_id: parseInt(form.sped_teacher_id, 10),
    };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    });

    try {
      await updateStudent(id, payload);
      navigate('/students');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update student.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <Loader text="Loading student data..." />;

  const selectedSchool = schools.find((s) => String(s.id) === form.school_id);
  const selectedTeacher = teachers.find((t) => String(t.id) === form.sped_teacher_id);

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,64rem)] space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/students"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Edit Student</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm">Update student information</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <Section title="Profile Photo">
          <div className="relative">
            <ProfileImageUpload
              hasProfileImage={hasProfileImage}
              entityType="student"
              entityId={Number(id)}
              imageRefreshKey={imageRefreshKey}
              initials={
                (form.student_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)) || '??'
              }
              previewFile={profileImage}
              onFileSelect={handleImageSelect}
              onRemove={handleRemoveImage}
            />
            {imageUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Uploading...
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="School & Assignment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="School *">
              <select required value={form.school_id} onChange={set('school_id')} className={inputCls}>
                <option value="">Select school</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>{s.school_name}</option>
                ))}
              </select>
              <SchoolHint school={selectedSchool} />
            </Field>
            <Field label="SPED Teacher *">
              <select
                required
                value={form.sped_teacher_id}
                onChange={set('sped_teacher_id')}
                className={inputCls}
                disabled={!form.school_id}
              >
                <option value="">
                  {form.school_id ? 'Select SPED teacher' : 'Select a school first'}
                </option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {[t.first_name, t.last_name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
              <TeacherHint teacher={selectedTeacher} />
              <p className="mt-1 text-xs text-gray-500">
                Case manager in compliance is taken from the SPED teacher / IEP documents.
              </p>
            </Field>
          </div>
        </Section>

        <Section title="Student Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Student ID">
              <input
                type="text"
                maxLength={30}
                value={form.student_id}
                onChange={set('student_id')}
                placeholder="e.g. MAS2013G7"
                className={inputCls}
              />
            </Field>
            <Field label="Student Name *">
              <input
                type="text"
                required
                value={form.student_name}
                onChange={set('student_name')}
                placeholder="Jane Doe"
                className={inputCls}
              />
            </Field>
            <Field label="Grade *">
              <select required value={form.grade} onChange={set('grade')} className={inputCls}>
                <option value="">Select grade</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </Field>
            <Field label="Date of Birth *">
              <DatePicker
                value={form.date_of_birth}
                onChange={(v) => setForm((prev) => ({ ...prev, date_of_birth: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                required
                aria-label="Date of Birth"
              />
            </Field>
          </div>
        </Section>

        <Section title="IEP & Reevaluation Dates">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="IEP Due Date">
              <DatePicker
                value={form.iep_due_date}
                onChange={(v) => setForm((prev) => ({ ...prev, iep_due_date: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                aria-label="IEP Due Date"
              />
            </Field>
            <Field label="Reevaluation Due Date">
              <DatePicker
                value={form.reevaluation_due_date}
                onChange={(v) => setForm((prev) => ({ ...prev, reevaluation_due_date: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                aria-label="Reevaluation Due Date"
              />
            </Field>
          </div>
        </Section>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-slate-600 text-sm font-medium transition-colors text-center"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20 text-center"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}